import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { ReimburseService } from '../../../../core/api/reimburse.service';
import { ReimburseLogService } from '../../../../core/api/reimburse-log.service';
import { TagService } from '../../../../core/api/tag.service';
import { FileService } from '../../../../core/api/file.service';
import { HelpersService } from '../../../../core/services/helpers.service';
import { AuthService } from '../../../../core/services/auth.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { ConfirmApprovalComponent } from '../../reimbursement-approval/confirm-approval/confirm-approval.component';
import { DetailApprovalDialogComponent } from '../../reimbursement-approval/action-reimbursement-approval/detail-approval-dialog/detail-approval-dialog.component';
import { PaymentMethodComponent } from '../payment-method/payment-method.component';
import { ProfileDialogComponent } from '../../../../components/profile-dialog/profile-dialog.component';
import { FilePreviewComponent } from '../../../../components/file-preview/file-preview.component';
import moment from 'moment/moment';

@Component({
  selector: 'app-action-reimbursement-finance',
  standalone: true,
  imports: [CommonModule, MatTableModule],
  templateUrl: './action-reimbursement-finance.component.html',
  styleUrl: './action-reimbursement-finance.component.scss',
})
export class ActionReimbursementFinanceComponent {
  displayedColumns: string[] = [
    'user',
    'team',
    'sequence',
    'date',
    'status',
    'notes',
    'action',
  ];
  reimburse: any = null;
  reimburseLogs: any[] = [];
  tag: any[] = [];
  approvalList: any[] = [];
  latestApproval: any = null;
  id: any;
  m: string;
  currentUser: any;
  finishApprove = false;

  constructor(
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private reimburseService: ReimburseService,
    private reimburseLogService: ReimburseLogService,
    private tagService: TagService,
    private fileService: FileService,
    private helpers: HelpersService,
    private authService: AuthService,
    private loadingService: LoadingService
  ) {
    this.id = this.activatedRoute.snapshot.queryParams['id'];
    this.m = this.activatedRoute.snapshot.queryParams['m'];
    this.currentUser = this.authService.getLoggedUser();
    this.getReimburse();
    this.getTag();
  }

  getReimburse() {
    this.loadingService.startLoading();
    this.reimburseService.getById(this.id).then(
      (resp: any) => {
        this.reimburse = resp;
        const logPromise = this.getReimburseLog(this.reimburse.id);
        const approvalListPromise = this.getApprovalList(this.reimburse.id);

        Promise.all([logPromise, approvalListPromise]).finally(() => {
          this.loadingService.endLoading();
        });
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  getReimburseLog(reimburseId: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reimburseLogService.getReimburseLog(reimburseId).then(
        (resp: any) => {
          this.reimburseLogs = resp.map((item: any) => ({
            ...item,
            updated_at: this.toLogDate(item.updated_at),
            created_at: this.toLogDate(item.created_at),
          }));
          const imagePromises = this.reimburseLogs.map((item: any) =>
            this.getImageUrl(item.creator)
          );

          Promise.all(imagePromises).finally(() => {
            resolve();
          });
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getApprovalList(reimburseId: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reimburseService.getApprovalList(reimburseId).then(
        (resp: any) => {
          this.approvalList = resp;
          if (this.approvalList.length) {
            this.latestApproval =
              this.approvalList[this.approvalList.length - 1].approval_note;
          }
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getTag() {
    this.loadingService.startLoading();
    this.tagService.getAllData().then(
      (res) => {
        this.tag = res;
        this.loadingService.endLoading();
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  private getImageUrl(item: any): Promise<void> {
    return new Promise((resolve) => {
      if (item.file) {
        this.loadingService.startLoading();
        this.fileService.download(item.file?.id).subscribe(
          (res) => {
            this.loadingService.endLoading();
            item['imageUrl'] = URL.createObjectURL(res);
            resolve();
          },
          (error) => {
            this.loadingService.endLoading();
            item['imageUrl'] = null;
            resolve();
          }
        );
      } else {
        item['imageUrl'] = null;
        resolve();
      }
    });
  }

  download() {
    const files = this.getFileItems(this.reimburse);
    if (files.length) {
      if (files.length === 1) {
        this.openFile(files[0]);
        return;
      }

      files.forEach((item: any) => this.openFileInNewTab(item));
    } else {
      this.helpers.sbError('Tidak ada file yang bisa diunduh');
    }
  }

  openFile(file: any) {
    if (file) {
      const isMobile = window.innerWidth < 768;
      const canPreviewInline = this.helpers.canPreviewInline(file);

      if (isMobile || !canPreviewInline) {
        this.openFileInNewTab(file);
        return;
      }

      this.dialog
        .open(FilePreviewComponent, {
          width: '70vw',
          maxWidth: '70vw',
          maxHeight: '95vh',
          data: { file },
        })
        .afterClosed()
        .subscribe((item) => {
          if (item) {
          }
        });
    }
  }

  private openFileInNewTab(file: any) {
    this.loadingService.startLoading();
    this.fileService.download(file?.id).subscribe(
      (res) => {
        this.loadingService.endLoading();
        this.helpers.openBlobInNewTab(res, file);
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  getFileItems(element: any): any[] {
    return this.helpers.normalizeFileItems(element);
  }

  approve(): void {
    console.log('askgfqawjfqw');
    this.dialog
      .open(PaymentMethodComponent, {
        width: '500px',
        maxHeight: '95vh',
      })
      .afterClosed()
      .subscribe((item) => {
        console.log(item);
        if (item) {
          const payload = item;
          payload['reimburseId'] = parseInt(this.id);
          payload['userId'] = this.currentUser.id;
          this.loadingService.startLoading();
          this.reimburseService
            .approveFinance(payload)
            .then((resp: any) => {
              this.loadingService.endLoading();
              this.helpers.sbSuccess('Reimbursement approved');
              this.getReimburseLog(this.id);
              this.getApprovalList(this.id);
              this.finishApprove = true;
            })
            .catch((err: any) => {
              this.loadingService.endLoading();
              this.helpers.sbError(err.message);
            });
        }
      });
  }

  reject(): void {
    this.dialog
      .open(ConfirmApprovalComponent, {
        width: '500px',
        maxHeight: '95vh',
        data: {
          expenseCategory: this.reimburse?.expanseCategory
        }
      })
      .afterClosed()
      .subscribe((item) => {
        if (item) {
          const payload = item;
          payload['reimburseId'] = parseInt(this.id);
          payload['userId'] = this.currentUser.id;
          this.loadingService.startLoading();
          this.reimburseService
            .reject(payload)
            .then((resp: any) => {
              this.loadingService.endLoading();
              this.helpers.sbSuccess('Reimbursement finance rejected!');
              this.getReimburseLog(this.id);
              this.getApprovalList(this.id);
            })
            .catch((err: any) => {
              this.loadingService.endLoading();
              this.helpers.sbError(err.message);
            });
        }
      });
  }

  detail(data: any, sequence: any): void {
    data['sequence'] = sequence;
    this.dialog
      .open(DetailApprovalDialogComponent, {
        width: '700px',
        maxHeight: '95vh',
        data,
      })
      .afterClosed()
      .subscribe((item) => {
        if (item) {
        }
      });
  }

  checkCanApprove() {
    const noSequence = this.reimburseLogs
      .slice(1)
      .some((item) => item.created_by == this.currentUser.id);
    const pic = this.reimburse
      ? this.reimburse.pic
        ? this.reimburse.pic.id
        : null
      : null;
    const approvalSequence = this.reimburse
      ? this.reimburse.approvalSequence
      : null;
    return (
      !this.finishApprove &&
      this.reimburse &&
      (this.reimburse.status == 'Sent' ||
        this.reimburse.status == 'Approved') &&
      ((approvalSequence && pic == this.currentUser.id) ||
        (!approvalSequence && noSequence))
    );
  }

  openProfile(userId: any) {
    this.dialog
      .open(ProfileDialogComponent, {
        maxWidth: '70vw',
        maxHeight: '95vh',
        data: userId,
      })
      .afterClosed()
      .subscribe((item) => {});
  }

  getPageLabel(): string {
    return 'Reimbursement finance';
  }

  getStatusClass(status?: string | null): string {
    switch (status) {
      case 'Draft':
        return 'status-badge status-badge--draft';
      case 'Sent':
        return 'status-badge status-badge--sent';
      case 'Approved':
        return 'status-badge status-badge--approved';
      case 'Rejected':
      case 'Rejected Finance':
        return 'status-badge status-badge--rejected';
      case 'Sent To Finance':
        return 'status-badge status-badge--finance-pending';
      case 'Finance Approve':
        return 'status-badge status-badge--finance-approved';
      case 'Reimbursed':
        return 'status-badge status-badge--disbursed';
      case 'Closed':
        return 'status-badge status-badge--draft';
      default:
        return 'status-badge status-badge--draft';
    }
  }

  getApprovalStatusClass(status: boolean | null | undefined): string {
    if (status === true) return 'status-badge status-badge--disbursed';
    if (status === false) return 'status-badge status-badge--rejected';
    return 'status-badge status-badge--draft';
  }

  getApprovalStatusLabel(status: boolean | null | undefined): string {
    if (status === true) return 'Approved';
    if (status === false) return 'Rejected';
    return '-';
  }

  isLogRejected(item: any): boolean {
    return !!item.description?.includes('Rejected');
  }

  getLogAccentClass(item: any): string {
    if (this.isLogRejected(item)) return 'timeline-accent--rejected';
    if (item.created_by == this.reimburse?.created_by) {
      return 'timeline-accent--creator';
    }
    if (this.authService.isFinance(item.creator)) return 'timeline-accent--finance';
    return 'timeline-accent--approver';
  }

  private toLogDate(value: any): Date | null {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  getLogTimestamp(item: any): Date | null {
    return this.toLogDate(item.updated_at) ?? this.toLogDate(item.created_at);
  }

  isWaitingLog(item: any): boolean {
    return !!item.description?.includes('Waiting');
  }
}
